import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import ButtonComponent from "../generic/ButtonComponent";
import Modal from "../generic/Modal/Modal";
import { SHOPIFY_REAUTH_TRIGGER_ENABLED } from "../../constants/featureFlags";
import { isKioskMode } from "../../helpers/posModeHelpers";
import { useFeatureFlag } from "../../hooks/globalSettingsHooks";
import { useFetchShopifyReauthUrl } from "../../hooks/shopifyReauthHooks";
import { API_V2_BASE_URL } from "../../constants/api";
import { useStoreDetails } from "../../hooks/storeHooks";

interface ShopifyTokenGuardProps {
  children: React.ReactNode;
}

function ShopifyTokenGuard({ children }: ShopifyTokenGuardProps) {
  const isEnabled = useFeatureFlag(SHOPIFY_REAUTH_TRIGGER_ENABLED);
  const { customerId } = useStoreDetails();
  const queryClient = useQueryClient();
  const { data } = useFetchShopifyReauthUrl(isEnabled && !isKioskMode());
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("shopifyReauthDismissed") === "true"
  );
  const [reauthSuccess, setReauthSuccess] = useState(false);
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries(["shopifyReauthUrl"]);
      setReauthSuccess(true);
    };
    window.addEventListener("reauth-success", handler);
    return () => window.removeEventListener("reauth-success", handler);
  }, [queryClient]);

  const isOnAuthCallback = window.location.hash.startsWith("#/shopifyAppAuth");

  if (!isEnabled || isKioskMode() || dismissed || isOnAuthCallback) {
    return <>{children}</>;
  }

  if (reauthSuccess) {
    const dismiss = () => {
      setReauthSuccess(false);
      window.history.replaceState({}, document.title, window.location.hash);
    };
    return (
      <>
        <Modal onClose={dismiss}>
          <Modal.Header>Re-Authentication Successful</Modal.Header>
          <Modal.Content>
            <p>
              Your BinderPOS account has been successfully re-authenticated with
              Shopify.
            </p>
          </Modal.Content>
          <Modal.Actions>
            <ButtonComponent primary fullWidth onClick={dismiss}>
              Continue
            </ButtonComponent>
          </Modal.Actions>
        </Modal>
        {children}
      </>
    );
  }

  // Intentionally blocking: children are NOT rendered while reauth is required per spec.
  // (Contrast with the success branch above, where children render behind the modal.)
  if (data?.status === "reauth_required") {
    return (
      <Modal
        onClose={() => {
          setDismissed(true);
          sessionStorage.setItem("shopifyReauthDismissed", "true");
        }}
      >
        <Modal.Header>Re-Authentication Required</Modal.Header>
        <Modal.Content>
          <p>
            To implement Shopify&apos;s new Security API, you will need to
            re-authenticate your instance of BinderPOS. The re-authorization
            process is simple, it only takes a single keypress. Press the
            Re-Authenticate button to proceed.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <ButtonComponent
            primary
            fullWidth
            onClick={() => {
              window.location.href = `${API_V2_BASE_URL}/shopifyApps/portal/reauth-redirect?customerId=${customerId}`;
            }}
          >
            Re-Authenticate
          </ButtonComponent>
        </Modal.Actions>
      </Modal>
    );
  }

  return <>{children}</>;
}

export default ShopifyTokenGuard;
